import { Drawer, Button, Input, Switch, Select } from 'antd';
import closeIcon from '../../../../assets/svg/close-icon.svg';
import getFormatMessage from '../../../../locales/get-format-message';
import {
  CommonStatusStep,
  IAPIServicesApproveItem,
  DevopsProject,
  IAPIApproveGameListItem,
} from '../../../../model/api-modules';
import './style.scss';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { IServicesApproveItem } from '../../../../model/service';
import classNames from 'classnames';
import { IServiceBatchApprovalParams } from '../../../../model/api-modules/env';
import { getBatchApprovalVersion, getEnvRegion } from '../../../../api';
import { getConfig } from '../../../../config';
import { AppEnvType, DeployArea } from '../../../../model';
import { messageWarning } from '../../../../components/message';
// import { fetchAdminEnvList } from '../../../../api/admin/game';
import { wrapRequestArea } from '../../../../lib/http/lib';
import { CustomPopover } from '../../../../components/custom-popover';

enum DrawerType {
  View = 'view',
  Approve = 'approve',
}

interface IProps {
  readonly site?: string;
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly onOk: (body: IServiceBatchApprovalParams) => Promise<void>;
  readonly selectedRowKeys: number[];
  readonly serviceList: IAPIServicesApproveItem[];
  readonly selectGameAndEnv: Record<string, string | Number>;
  readonly type: DrawerType | null;
  readonly showConfirm: (value: boolean) => void;
  readonly gameList: IAPIApproveGameListItem[];
}

export const BatchDrawer = forwardRef((props: IProps, ref) => {
  const { site } = props;
  const [gameCode, setGameCode] = useState<string>('');
  const [accessLayerChecked, setAccessLayerChecked] = useState<boolean>(false);
  const [bianQueChecked, setBianQueChecked] = useState<boolean>(false);
  const [reportGatewayChecked, setReportGatewayChecked] =
    useState<boolean>(false);
  const [version, setVersion] = useState<string>('');
  const [g6DomainURL, setG6DomainURL] = useState<string>('');
  const [versionList, setVersionList] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const appConfig = getConfig();
  const { deployArea } = appConfig;
  const isTest = appConfig.env === AppEnvType.Test;

  const onClose = () => {
    setGameCode('');
    setAccessLayerChecked(false);
    setBianQueChecked(false);
    setReportGatewayChecked(false);
    setVersion('');
    setG6DomainURL('');
    setVersionList([]);
    props.onClose();
  };

  const getApprovalVersion = async () => {
    const serviceItem = props.serviceList.find(
      (item) => item.id === props.selectedRowKeys[0]
    );
    if (serviceItem?.GameID && serviceItem?.EnvID) {
      let devopsProject = '';
      if (isTest) {
        devopsProject = DevopsProject.Test;
      } else if (deployArea === DeployArea.singapore) {
        devopsProject = DevopsProject.Singapore;
      } else {
        devopsProject = DevopsProject.Cn;
      }
      const res = await wrapRequestArea(
        () =>
          getBatchApprovalVersion(
            serviceItem.GameID,
            serviceItem.EnvID.toString(),
            devopsProject,
            devopsProject,
            'main-gaas',
            g6DomainURL
          ),
        site
      );
      if (res?.data && res.data.length !== 0) {
        setVersionList(res.data || []);
      }
    }
  };

  const getEnvRegionData = async (region: string) => {
    const res = await wrapRequestArea(getEnvRegion, site);
    if (res && res.config_env_region?.Value?.length !== 0 && region) {
      const configItem = res.config_env_region.Value.find(
        (item) => item.value === region
      );
      if (configItem) {
        const { domain } = configItem;
        setG6DomainURL(domain);
        return;
      }
    }
    setG6DomainURL('');
  };

  useEffect(() => {
    getApprovalVersion();
  }, [g6DomainURL]);

  const getGameList = async () => {
    if (props.selectGameAndEnv.studioId && props.selectGameAndEnv.gameId) {
      if (props.serviceList) {
        const gameItem = props.gameList.find(
          (item) => item.GameID === props.selectGameAndEnv.gameId
        );
        if (gameItem) {
          setGameCode(gameItem.ProjectCode || '');
        }
      }
    }
  };

  useEffect(() => {
    if (props.visible) {
      getGameList();
      if (props.selectGameAndEnv.gameId && props.selectGameAndEnv.envId) {
        if (props.type === DrawerType.View) {
          const currentService = props.serviceList.find(
            (item) =>
              item.id === props.selectedRowKeys[0] && item.ApprovalPayload
          );
          if (currentService) {
            const { extraFields } = JSON.parse(
              currentService.ApprovalPayload || '{}'
            );
            if (Object.keys(extraFields).length !== 0) {
              setAccessLayerChecked(extraFields.enableAccess);
              setBianQueChecked(extraFields.enableBianque);
              setReportGatewayChecked(extraFields.reportGateway);
              setVersion(extraFields.chartVersion);
            }
          }
        } else {
          const currentService =
            props.serviceList.filter(
              (item) =>
                item.GameID === props.selectGameAndEnv.gameId &&
                item.EnvID === props.selectGameAndEnv.envId &&
                item.ApprovalPayload &&
                item.Status === CommonStatusStep.Resolve
            ) || [];
          if (currentService.length > 1) {
            currentService.sort(
              (a, b) =>
                Number(new Date(b.CreateTime)) - Number(new Date(a.CreateTime))
            );
          }
          if (
            currentService.length !== 0 &&
            currentService[0].ApprovalPayload
          ) {
            const { extraFields } = JSON.parse(
              currentService[0].ApprovalPayload || '{}'
            );
            if (Object.keys(extraFields).length !== 0) {
              setAccessLayerChecked(extraFields.enableAccess);
              setBianQueChecked(extraFields.enableBianque);
              setReportGatewayChecked(extraFields.reportGateway);
              setVersion(extraFields.chartVersion);
            }
          }
        }
      }
    }
  }, [props.visible]);

  useEffect(() => {
    const serviceItem = props.serviceList.find(
      (item) => item.id === props.selectedRowKeys[0]
    );
    if (serviceItem) {
      const envItem = serviceItem?.EnvItem;
      if (envItem?.Region) {
        getEnvRegionData(envItem.Region);
        return;
      }
    }
    setG6DomainURL('');
  }, [props.serviceList, props.selectedRowKeys, props.visible]);

  const onClickOk = async (
    isNoDeployEnabled: boolean,
    noDeployCheck?: boolean
  ) => {
    const selectedRows: IServicesApproveItem[] = props.serviceList.filter(
      (serviceItem) => props.selectedRowKeys.includes(serviceItem.id)
    );
    let recordIds: number[] = [];
    selectedRows.forEach((item) => {
      if (item.relatedApproveIds && item.relatedApproveIds.length !== 0) {
        recordIds = recordIds.concat(item.relatedApproveIds);
      } else {
        recordIds.push(item.id);
      }
    });
    if (!gameCode) {
      return messageWarning('游戏代号不存在');
    }
    if (!g6DomainURL) {
      return messageWarning('请输入g6DomainURL');
    }
    if (!version) {
      return messageWarning('请选择版本号');
    }
    if (noDeployCheck) {
      props.showConfirm(true);
      return;
    }
    const serviceItem = props.serviceList.find(
      (item) => item.id === props.selectedRowKeys[0]
    );
    if (serviceItem) {
      setLoading(true);
      const envType = serviceItem.EnvItem?.Type || '';
      // const result = await wrapRequestArea(
      //   () => fetchAdminEnvList(serviceItem.GameID),
      //   site
      // );
      // if (result) {
      //   const envItem = result.find((item) => item.id === serviceItem.EnvID);
      //   envType = envItem?.Type || '';
      // }
      const body = {
        recordIds,
        status: CommonStatusStep.Resolve,
        expectedAmount: 0,
        envType,
        projectCode: gameCode,
        chartVersion: version,
        enableBianque: bianQueChecked,
        enableAccess: accessLayerChecked,
        reportGateway: reportGatewayChecked,
        g6DomainURL,
        isNoDeployEnabled,
      };
      props.onOk(body);
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    onClickOk,
  }));

  // const handleGameCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   setGameCode(e.target.value);
  // };

  const handleAccessLayerChange = (checked: boolean) => {
    setAccessLayerChecked(checked);
  };

  const handleBianQueChange = (checked: boolean) => {
    setBianQueChecked(checked);
  };

  const handleReportGatewayChange = (checked: boolean) => {
    setReportGatewayChecked(checked);
  };

  const handleVersionChange = (value: string) => {
    setVersion(value);
  };

  const renderContent = () => {
    const serviceItem = props.serviceList.find(
      (item) => item.id === props.selectedRowKeys[0]
    );
    const selectedRows: IServicesApproveItem[] =
      props.serviceList.filter((serviceItem) =>
        props.selectedRowKeys.includes(serviceItem.id)
      ) || [];
    return (
      <div className="service-batch-drawer">
        <div className="drawer-title" style={{ marginTop: 0 }}>
          <span>{getFormatMessage('basic')}</span>
          <span className="drawer-title-icon">*</span>
        </div>
        <div className="drawer-game-env-title">
          <span>{getFormatMessage('title_name')}</span>
          <span>{getFormatMessage('env_name')}</span>
        </div>
        <div className="drawer-game-env-value">
          <span>{`${serviceItem?.GameName || ''}(${
            serviceItem?.GameID || ''
          })`}</span>
          <span>{`${serviceItem?.EnvItem?.Name || ''}(${
            serviceItem?.EnvID || ''
          })`}</span>
        </div>
        <div className="drawer-game-env-title">
          {getFormatMessage('title_code_name')}
        </div>
        <div className="drawer-game-env-value">
          <span>{gameCode}</span>
          {/* <Input
            onChange={handleGameCodeChange}
            value={gameCode}
            style={{ width: 200 }}
          /> */}
        </div>
        <div className="drawer-title">
          <span>{getFormatMessage('open_service')}</span>
          <span className="drawer-title-icon">*</span>
        </div>
        <div className="drawer-service-box">
          {selectedRows.length !== 0 &&
            selectedRows.map((item, index) => {
              const itemSub = item.SubServiceName?.split(',') || [];
              return (
                <div
                  className={classNames(
                    'drawer-service-item-box',
                    index !== 0 ? 'top-border' : ''
                  )}
                >
                  <div
                    className={classNames(
                      'drawer-service-item-left',
                      index === 0 ? 'first-item' : '',
                      index === selectedRows.length - 1 ? 'last-item' : '',
                      index === 0 && index === selectedRows.length - 1
                        ? 'first-last-item'
                        : ''
                    )}
                  >
                    {item.ServiceName}
                  </div>
                  <div className="drawer-service-item-right-box">
                    {itemSub.length !== 0 &&
                      itemSub.map((subItem) => {
                        return (
                          <div className="drawer-service-item-right">
                            {subItem}
                          </div>
                        );
                      })}
                  </div>
                </div>
              );
            })}
        </div>
        <div className="drawer-title">
          <span>{getFormatMessage('additional_config')}</span>
          <span className="drawer-title-icon">*</span>
        </div>
        <div style={{ display: 'flex' }}>
          <div style={{ flex: 1 }}>
            <div className="drawer-game-env-title">
              {getFormatMessage('access_layer_deployment')}
            </div>
            <div className="drawer-game-env-value">
              <Switch
                onChange={handleAccessLayerChange}
                checked={accessLayerChecked}
                disabled={props.type === DrawerType.View}
              />
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div className="drawer-game-env-title">
              {getFormatMessage('bianque_deployment')}
            </div>
            <div className="drawer-game-env-value">
              <Switch
                onChange={handleBianQueChange}
                checked={bianQueChecked}
                disabled={props.type === DrawerType.View}
              />
            </div>
          </div>
        </div>
        <div className="drawer-game-env-title">
          {getFormatMessage('report_gateway_deployment')}
        </div>
        <div className="drawer-game-env-value">
          <Switch
            onChange={handleReportGatewayChange}
            checked={reportGatewayChecked}
            disabled={props.type === DrawerType.View}
          />
        </div>
        <div className="drawer-game-env-title">g6DomainURL</div>
        <div className="drawer-game-env-value">
          <Input value={g6DomainURL} style={{ width: 300 }} disabled={true} />
        </div>
        <div className="drawer-game-env-title">
          {getFormatMessage('approval_version')}
          <span className="drawer-title-icon">*</span>
        </div>
        <div className="drawer-game-env-value-input">
          <Select
            onChange={handleVersionChange}
            value={version}
            style={{ width: 200 }}
            disabled={props.type === DrawerType.View}
          >
            {versionList.map((item) => (
              <Select.Option key={item.version} value={item.version}>
                {item.version}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>
    );
  };
  const serviceItem = props.serviceList.find(
    (item) => item.id === props.selectedRowKeys[0]
  );
  const envGos = JSON.parse(serviceItem?.EnvItem?.GosParams || '{}');
  return (
    <Drawer
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex' }}>
            {props.type === DrawerType.View
              ? '审批详情'
              : getFormatMessage('service_approval')}
          </div>
          <img src={closeIcon} className="sync-close-icon" onClick={onClose} />
        </div>
      }
      placement="right"
      width={720}
      closable={false}
      onClose={onClose}
      visible={props.visible}
      headerStyle={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        marginLeft: '3px',
      }}
      footer={
        props.type === DrawerType.View ? (
          <Button
            className="gc-btn gc-btn-default btn-admin sync-cancel"
            style={{ width: '80px', borderRadius: '4px' }}
            onClick={onClose}
          >
            {getFormatMessage('close')}
          </Button>
        ) : (
          <div style={{ display: 'flex' }}>
            <Button
              className="gc-btn gc-btn-default btn-admin sync-cancel"
              style={{ width: '80px', borderRadius: '4px' }}
              onClick={onClose}
            >
              {getFormatMessage('cancel')}
            </Button>
            {envGos?.EnvURL ? (
              <Button
                className="gc-btn gc-btn-default btn-admin"
                style={{
                  width: '110px',
                  borderRadius: '4px',
                  marginRight: '8px',
                  color: '#363dc8',
                  borderColor: '#363dc8',
                }}
                onClick={() => onClickOk(true, true)}
                loading={loading}
              >
                免部署开通
              </Button>
            ) : (
              <CustomPopover content="当前环境未开通，暂不支持免部署开通服务">
                <div
                  style={{
                    width: '110px',
                    border: '1px solid, #363dc8',
                    borderRadius: '4px',
                    marginRight: '8px',
                    color: '#363dc8',
                    borderColor: '#363dc8',
                    textAlign: 'center',
                    height: '32px',
                    lineHeight: '32px',
                    cursor: 'not-allowed',
                    opacity: '0.5',
                  }}
                >
                  免部署开通
                </div>
              </CustomPopover>
            )}
            <Button
              className="gc-btn gc-btn-primary btn-admin"
              onClick={() => onClickOk(false)}
              style={{ minWidth: '80px', borderRadius: '4px' }}
              loading={loading}
            >
              {getFormatMessage('approve')}
            </Button>
          </div>
        )
      }
    >
      {renderContent()}
    </Drawer>
  );
});
